import React, { useEffect, useRef, useState } from "react";
import * as PropTypes from "prop-types";
import classnames from "classnames";

import Backdrop from "../Backdrop/Backdrop";
import Portal from "../Portal/Portal";
import Scroll from "../Scroll/Scroll";

import useFocus from "../../hooks/useFocus";
import { handleKeyDownExitFloatWindow } from "../../utils/keyboard-navigation/keyboard-navigation";
import useRegulateGlobalClassNameIsAnyPopupLikeWindowOpened from "@/shared/hooks/useRegulateGlobalClassNameIsAnyPopupLikeWindowOpened";
import useLockBodyScroll from "@/shared/hooks/useLockBodyScroll";

import { EL_ID_PORTAL_MAIN_AFTER } from "../Portal/PortalMainAfter/constants";

import styles from "./SidePanel.module.scss";

const SidePanel = React.forwardRef(
  (
    {
      isUseLockBodyScroll,
      isHiddenByCSS = false,
      focusOnBecameOpen = true,
      isOpen,
      preventCoverHeader = true,
      onClose,
      className,
      children,
      side,
      contentProps,
      backdropProps,
      ...rootProps
    },
    ref
  ) => {
    const { className: contentClassName, tag: Tag = Scroll, ...contentRestProps } = contentProps;
    const { className: backdropClassName, ...backdropRestProps } = backdropProps;

    const refElRoot = useRef(null);

    const refFinal = ref ?? refElRoot;

    useLockBodyScroll(!!(isUseLockBodyScroll && isOpen));
    useFocus(focusOnBecameOpen && isOpen, refFinal);
    useRegulateGlobalClassNameIsAnyPopupLikeWindowOpened(!!isOpen);

    const [headerHeight, setHeaderHeight] = useState(0);
    useEffect(() => {
      setHeaderHeight(document.querySelector("header")?.offsetHeight);
    }, []);

    if (!isOpen && !isHiddenByCSS) {
      return null;
    }

    let propsContentOptional;

    if (Tag === Scroll) {
      propsContentOptional = {
        hasStaticPosition: true,
        style: {
          width: undefined, // reset default value "100%".
          ...contentProps?.style,
        },
      };
    }

    const handleKeyDownRoot = (e) => {
      if (isOpen && onClose) {
        handleKeyDownExitFloatWindow(e, onClose);
      }
    };

    const jsxPanel = (
      <div
        ref={refFinal}
        tabIndex={1}
        onKeyDown={handleKeyDownRoot}
        className={classnames(styles["root"], className, styles[side], {
          [styles["open"]]: isOpen,
          [styles["prevent-cover-header"]]: preventCoverHeader,
        })}
        {...rootProps}
        style={{
          ...rootProps.style,
          "--height-header": headerHeight + "px",
        }}
      >
        <Tag
          className={classnames(styles["content"], contentClassName)}
          {...contentRestProps}
          {...propsContentOptional}
        >
          {children}
        </Tag>
        <Backdrop
          className={classnames(styles["backdrop"], backdropClassName)}
          onClick={onClose}
          {...backdropRestProps}
        />
      </div>
    );

    if (isHiddenByCSS) {
      return (
        <div className="hidden" id={isHiddenByCSS}>
          {jsxPanel}
        </div>
      );
    }

    return <Portal id={EL_ID_PORTAL_MAIN_AFTER}>{jsxPanel}</Portal>;
  }
);

SidePanel.defaultProps = {
  side: "left",
  contentProps: {},
  backdropProps: {},
};

SidePanel.propTypes = {
  isOpen: PropTypes.bool,
  positionFixed: PropTypes.bool,
  preventCoverHeader: PropTypes.bool,
  children: PropTypes.node,
  className: PropTypes.string,
  side: PropTypes.string,
  contentProps: PropTypes.object,
  backdropProps: PropTypes.object,
};

export default SidePanel;
